import { MakeAPaymentApiException } from "raci-make-a-payment-clientproxy";
import { HTTP_STATUS_CODE_TIMEOUT, setIntervalGuarded, useSessionTimeRemaining } from "raci-react-library";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PolicyRoutes, UtilityRoutes } from "../../routing/routes.config";
import useApiClient from "../useApiClient";
import useExceptionHandler from "../useExceptionHandler";
import useHasValidSession from "../useHasValidSession";
import useOnExpiry from "../useOnExpiry";

export const EXPIRY_CHECK_INTERVAL_IN_MILLISECONDS = 30000; // 30 Seconds

export const useSessionExpiry = () => {
  const client = useApiClient();
  const location = useLocation();
  const onExpiry = useOnExpiry();
  const hasValidSession = useHasValidSession();
  const handleExceptionNavigation = useExceptionHandler();
  const { timeoutTime, setTimeoutTime } = useSessionTimeRemaining();
  const [wait, setWait] = useState(EXPIRY_CHECK_INTERVAL_IN_MILLISECONDS);

  /**
   * Skip expiry check if:
   * - Session ID is not valid
   * - Current location is SessionTimeout or Confirmation pages
   */
  const shouldSkipExpiryCheck =
    !hasValidSession ||
    location.pathname.endsWith(UtilityRoutes.SessionTimeout) ||
    location.pathname.endsWith(PolicyRoutes.Confirmation);

  /**
   * Check session if:
   * - Session ID is valid
   * - Current location is SessionTimeout or Confirmation pages
   */
  const checkSessionExpiry = async () => {
    const getNextDelay = (expiryInSeconds: number) =>
      Math.max(expiryInSeconds * 500, EXPIRY_CHECK_INTERVAL_IN_MILLISECONDS);

    if (!shouldSkipExpiryCheck) {
      try {
        const response = await client.getSessionExpiryInSeconds();

        const expiryInSeconds = parseInt(response.result);
        setWait(getNextDelay(expiryInSeconds));
        setTimeoutTime(expiryInSeconds > 0 ? new Date().getTime() + expiryInSeconds * 1000 : null);
      } catch (ex) {
        const exception = ex as MakeAPaymentApiException;
        if (exception?.status === HTTP_STATUS_CODE_TIMEOUT) {
          setWait(getNextDelay(-1));
          onExpiry();
        } else {
          handleExceptionNavigation("getSessionExpiryInSeconds", ex);
        }
      }
    }
  };

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled && !shouldSkipExpiryCheck) {
      const handle = setIntervalGuarded(async () => {
        if (!isCancelled) {
          await checkSessionExpiry();
        }
      }, wait);
      return () => {
        isCancelled = true;
        clearTimeout(handle);
      };
    }

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutTime, shouldSkipExpiryCheck]);

  return { checkSessionExpiry };
};

export default useSessionExpiry;
